(() => {
  const RECENT_SEARCHES_KEY = 'theme:recent-searches';
  const RECENT_SEARCHES_LIMIT = 5;
  const CACHE_LIMIT = 15;

  const debounce = (callback, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => callback(...args), wait);
    };
  };

  class PredictiveSearchForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-predictive-search-form]');
      this.input = this.querySelector('[data-predictive-search-input]');
      this.clearButton = this.querySelector('[data-predictive-search-clear]');
      this.loader = this.querySelector('[data-predictive-search-loader]');
      this.surface = this.querySelector('[data-predictive-search-surface]');
      this.initial = this.querySelector('[data-predictive-search-initial]');
      this.results = this.querySelector('[data-predictive-search-results]');
      this.status = this.querySelector('[data-predictive-search-status]');
      this.abortController = new AbortController();
      this.requestController = null;
      this.cache = new Map();
      this.activeIndex = -1;
      this.search = debounce(() => this.handleSearch(), 180);

      const { signal } = this.abortController;
      this.input?.addEventListener('input', () => this.handleInput(), { signal });
      this.input?.addEventListener('focus', () => this.handleFocus(), { signal });
      this.clearButton?.addEventListener('click', () => this.clear(), { signal });
      this.form?.addEventListener('submit', () => this.remember(this.input.value), { signal });
      this.addEventListener('click', event => this.handleClick(event), { signal });
      this.addEventListener('keydown', event => this.handleKeydown(event), { signal });
      document.addEventListener('pointerdown', event => this.handleOutsideClick(event), { signal });
      document.addEventListener('predictive-search:recent-change', () => this.renderRecent(), { signal });

      this.renderRecent();
      this.syncClearButton();
    }

    disconnectedCallback() {
      this.requestController?.abort();
      this.abortController?.abort();
    }

    get minimumCharacters() {
      return Number(this.dataset.minimumCharacters || 2);
    }

    get isInline() {
      return this.dataset.mode === 'inline';
    }

    handleInput() {
      this.syncClearButton();
      this.activeIndex = -1;

      if (this.input.value.trim().length < this.minimumCharacters) {
        this.requestController?.abort();
        this.setLoading(false);
        this.showInitial();
        this.open();
        return;
      }

      this.search();
    }

    handleFocus() {
      if (this.input.value.trim().length >= this.minimumCharacters && this.results?.hasChildNodes()) {
        this.showResults();
      } else {
        this.showInitial();
      }
      this.open();
    }

    async handleSearch() {
      const term = this.input.value.trim();
      if (term.length < this.minimumCharacters) return;

      const url = this.buildUrl(term);
      const cacheKey = url.toString();
      this.setLoading(true);

      try {
        let responseMarkup = this.cache.get(cacheKey);
        if (!responseMarkup) {
          this.requestController?.abort();
          this.requestController = new AbortController();
          const response = await fetch(url, {
            headers: { Accept: 'text/html' },
            signal: this.requestController.signal
          });
          if (!response.ok) throw new Error(`Predictive search request failed: ${response.status}`);
          responseMarkup = await response.text();
          this.addToCache(cacheKey, responseMarkup);
        }

        if (this.input.value.trim() !== term) return;
        this.renderResults(responseMarkup);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.renderError();
      } finally {
        if (this.input.value.trim() === term) this.setLoading(false);
      }
    }

    buildUrl(term) {
      const url = new URL(this.dataset.predictiveSearchUrl, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', this.dataset.resourceTypes);
      url.searchParams.set('resources[limit]', this.dataset.resourceLimit);
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');
      url.searchParams.set('section_id', this.dataset.sectionId);
      return url;
    }

    renderResults(markup) {
      const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
      const response = documentFragment.querySelector('[data-predictive-search-response]');
      if (!response) {
        this.renderError();
        return;
      }

      this.limitResults(response);
      if (this.dataset.showVendor !== 'true') {
        response.querySelectorAll('[data-search-vendor]').forEach(element => element.remove());
      }
      if (this.dataset.showPrice !== 'true') {
        response.querySelectorAll('[data-search-price]').forEach(element => element.remove());
      }
      if (this.dataset.showSoldOut !== 'true') {
        response.querySelectorAll('[data-search-sold-out]').forEach(element => element.remove());
      }

      this.results.replaceChildren(response);
      this.results.hidden = false;
      this.initial.hidden = true;
      this.classList.remove('has-results');
      void this.offsetWidth;
      this.classList.add('has-results');
      this.surface.hidden = false;
      this.open();
      this.input.setAttribute('aria-expanded', 'true');
      this.status.textContent = this.dataset.resultsMessage;
    }

    limitResults(response) {
      response.querySelectorAll('[data-result-group]').forEach(group => {
        const limit = group.dataset.resultGroup === 'product'
          ? Number(this.dataset.productLimit)
          : Number(this.dataset.otherLimit);
        [...group.querySelectorAll('[data-result-item]')].forEach((item, index) => {
          if (index >= limit) item.remove();
        });
        if (!group.querySelector('[data-result-item]')) group.remove();
      });
    }

    renderError() {
      const message = document.createElement('p');
      message.className = 'predictive-search-results__error';
      message.textContent = this.dataset.errorMessage;
      this.results.replaceChildren(message);
      this.results.hidden = false;
      this.initial.hidden = true;
      this.surface.hidden = false;
      this.open();
      this.input.setAttribute('aria-expanded', 'true');
      this.status.textContent = this.dataset.errorMessage;
    }

    handleClick(event) {
      const termLink = event.target.closest('[data-predictive-search-term]');
      if (termLink) this.remember(termLink.dataset.predictiveSearchTerm);
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        if (this.input.value) {
          this.clear();
        } else {
          this.close();
          this.input.blur();
        }
        return;
      }

      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      const options = this.getOptions();
      if (!options.length) return;
      event.preventDefault();

      const currentIndex = options.indexOf(document.activeElement);
      if (event.key === 'ArrowDown') {
        this.activeIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % options.length;
      } else {
        this.activeIndex = currentIndex < 0 ? options.length - 1 : (currentIndex - 1 + options.length) % options.length;
      }

      options.forEach(option => option.setAttribute('aria-selected', 'false'));
      const activeOption = options[this.activeIndex];
      activeOption.setAttribute('aria-selected', 'true');
      activeOption.focus({ preventScroll: true });
    }

    getOptions() {
      return [...this.querySelectorAll('[data-predictive-search-option]')]
        .filter(option => option.offsetParent !== null);
    }

    handleOutsideClick(event) {
      if (!this.isInline || !this.classList.contains('is-open') || this.contains(event.target)) return;
      this.close();
    }

    clear() {
      this.input.value = '';
      this.requestController?.abort();
      this.results.replaceChildren();
      this.classList.remove('has-results', 'is-loading');
      this.syncClearButton();
      this.showInitial();
      this.open();
      this.input.focus();
    }

    showInitial() {
      this.initial.hidden = false;
      this.results.hidden = true;
      this.surface.hidden = false;
      this.input.setAttribute('aria-expanded', this.isInline ? 'true' : 'false');
      this.renderRecent();
    }

    showResults() {
      this.initial.hidden = true;
      this.results.hidden = false;
      this.surface.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    open() {
      if (!this.isInline) return;
      this.surface.hidden = false;
      this.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
      document.body.classList.add('predictive-search-inline-open');
    }

    close() {
      if (!this.isInline) return;
      this.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('predictive-search-inline-open');
      window.setTimeout(() => {
        if (!this.classList.contains('is-open')) this.surface.hidden = true;
      }, 180);
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      this.surface.setAttribute('aria-busy', String(isLoading));
      this.loader.hidden = !isLoading;
      if (isLoading) this.clearButton.hidden = true;
      else this.syncClearButton();
    }

    syncClearButton() {
      if (!this.clearButton) return;
      this.clearButton.hidden = this.input.value.length === 0;
    }

    addToCache(key, markup) {
      if (this.cache.size >= CACHE_LIMIT) this.cache.delete(this.cache.keys().next().value);
      this.cache.set(key, markup);
    }

    readRecent() {
      try {
        const stored = JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
        return Array.isArray(stored) ? stored.filter(term => typeof term === 'string') : [];
      } catch {
        return [];
      }
    }

    remember(term) {
      const normalizedTerm = term?.trim();
      if (!normalizedTerm) return;
      const recent = this.readRecent().filter(item => item.toLocaleLowerCase() !== normalizedTerm.toLocaleLowerCase());
      recent.unshift(normalizedTerm);
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, RECENT_SEARCHES_LIMIT)));
        document.dispatchEvent(new CustomEvent('predictive-search:recent-change'));
      } catch {
        // Search remains fully functional when browser storage is unavailable.
      }
    }

    renderRecent() {
      const section = this.querySelector('[data-predictive-search-recent]');
      const list = this.querySelector('[data-predictive-search-recent-list]');
      const clear = this.querySelector('[data-predictive-search-clear-recent]');
      if (!section || !list) return;

      const recent = this.readRecent();
      section.hidden = recent.length === 0;
      list.replaceChildren(...recent.map(term => {
        const link = document.createElement('a');
        const url = new URL(this.dataset.searchUrl, window.location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('options[prefix]', 'last');
        link.className = 'predictive-search-chip';
        link.href = url.toString();
        link.textContent = term;
        link.dataset.predictiveSearchTerm = term;
        return link;
      }));

      if (clear && !clear.dataset.bound) {
        clear.dataset.bound = 'true';
        clear.addEventListener('click', () => {
          try {
            localStorage.removeItem(RECENT_SEARCHES_KEY);
          } catch {
            // Nothing else is required when browser storage is unavailable.
          }
          document.dispatchEvent(new CustomEvent('predictive-search:recent-change'));
          this.input.focus();
        }, { signal: this.abortController.signal });
      }
    }
  }

  class PredictiveSearchDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-predictive-search-dialog]');
      this.panel = this.querySelector('[data-predictive-search-panel]');
      this.inlineTemplate = this.querySelector('[data-predictive-search-inline-template]');
      this.abortController = new AbortController();
      this.trigger = null;

      const { signal } = this.abortController;
      document.addEventListener('click', event => {
        const opener = event.target.closest('[data-predictive-search-open]');
        if (!opener) return;
        event.preventDefault();
        this.open(opener);
      }, { signal });
      this.querySelectorAll('[data-predictive-search-close]').forEach(button => {
        button.addEventListener('click', () => this.close(), { signal });
      });
      this.dialog?.addEventListener('cancel', event => {
        event.preventDefault();
        this.close();
      }, { signal });
      this.dialog?.addEventListener('keydown', event => {
        window.ThemeComponents?.focus.trapTab(event, this.panel);
      }, { signal });
      document.addEventListener('shopify:section:load', () => this.mountInlineSearch(), { signal });

      this.mountInlineSearch();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.unmountInlineSearch();
    }

    mountInlineSearch() {
      this.unmountInlineSearch();
      if (this.dataset.displayMode !== 'inline' || !this.inlineTemplate) return;

      document.querySelectorAll('[data-predictive-search-inline-slot]').forEach(slot => {
        const fragment = this.inlineTemplate.content.cloneNode(true);
        const form = fragment.querySelector('predictive-search-form');
        form.dataset.predictiveSearchInlineMounted = '';
        slot.querySelectorAll('[data-predictive-search-open]').forEach(opener => { opener.hidden = true; });
        slot.append(fragment);
        slot.classList.add('has-inline-search');
      });
    }

    unmountInlineSearch() {
      document.querySelectorAll('[data-predictive-search-inline-mounted]').forEach(form => form.remove());
      document.querySelectorAll('[data-predictive-search-inline-slot]').forEach(slot => {
        slot.classList.remove('has-inline-search');
        slot.querySelectorAll('[data-predictive-search-open]').forEach(opener => { opener.hidden = false; });
      });
      document.body.classList.remove('predictive-search-inline-open');
    }

    open(trigger) {
      if (!this.dialog || this.dialog.open) return;
      this.trigger = trigger;
      document.querySelectorAll('predictive-search-form.is-open').forEach(form => form.close());
      document.querySelectorAll('.mobile-nav[open]').forEach(menu => menu.removeAttribute('open'));
      this.dialog.showModal();
      document.body.classList.add('predictive-search-open');
      requestAnimationFrame(() => {
        this.dialog.classList.add('is-open');
        this.dialog.querySelector('[data-predictive-search-input]')?.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.dialog?.open) return;
      this.dialog.classList.remove('is-open');
      document.body.classList.remove('predictive-search-open');
      window.setTimeout(() => {
        this.dialog.close();
        window.ThemeComponents?.focus.restore(this.trigger);
      }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 280);
    }
  }

  if (!customElements.get('predictive-search-form')) {
    customElements.define('predictive-search-form', PredictiveSearchForm);
  }
  if (!customElements.get('predictive-search-drawer')) {
    customElements.define('predictive-search-drawer', PredictiveSearchDrawer);
  }
})();
